import React from "react";
import { Boxes, CircleDot, PackageCheck } from "lucide-react";

import { buildHardwareManifest } from "./scratchpadModel";
import { DerivedExportToolbar } from "./DerivedExportToolbar";
import { SectionDispatchBadge } from "./SectionDispatchBadge";

export const HardwareManifest = ({ sheet, sections, suppliers, setSuppliers, canEdit, onSheetUpdate, dirty }) => {
  const manifest = buildHardwareManifest(sections);

  return (
    <div className="space-y-4" data-testid="automated-hardware-manifest">
      <div className="flex flex-wrap items-center gap-2"><h2 className="text-base font-semibold text-slate-900">Automated Hardware Manifest</h2><SectionDispatchBadge sectionId="hardware_manifest" status={sheet.shop_floor_section_status} /></div>
      <DerivedExportToolbar tab="hardware" sheet={sheet} suppliers={suppliers} setSuppliers={setSuppliers} canDispatch={canEdit} dirty={dirty} onSheetUpdate={onSheetUpdate} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border border-sky-200 bg-sky-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-sky-800"><CircleDot className="h-4 w-4" />Hinge & Baseplate Packages</div>
          <p className="mt-2 font-mono text-2xl font-bold text-slate-900" data-testid="hardware-hinge-total">{manifest.totals.hinge_baseplate_packages}</p>
          <p className="mt-1 text-xs text-slate-600">2 packages through 36″ · 3 through 48″ · 4 above 48″</p>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800"><Boxes className="h-4 w-4" />Salice Slide Pairs</div>
          <p className="mt-2 font-mono text-2xl font-bold text-slate-900" data-testid="hardware-slide-total">{manifest.totals.salice_slide_pairs}</p>
          <p className="mt-1 text-xs text-slate-600">Depth + 0.75″ · nearest even size · clamped 10″–24″</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white" data-testid="hardware-manifest-ledger">
        <div className="hidden sm:block">
          <div className="grid grid-cols-[1fr_140px_120px] border-b bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
            <span>Inventory Item</span><span>Size</span><span className="text-right">Quantity</span>
          </div>
          {manifest.lines.map((line, index) => (
            <div key={line.line_id} className="grid grid-cols-[1fr_140px_120px] items-center border-b px-3 py-3 last:border-b-0" data-testid={`hardware-line-${index}`}>
              <div className="min-w-0">
                <p className="flex items-center gap-2 truncate text-sm font-semibold text-slate-900"><PackageCheck className="h-4 w-4 shrink-0 text-emerald-600" />{line.item_name}</p>
                {line.breakdown && <p className="mt-1 text-xs text-slate-500">2-per-door: {line.breakdown["2_per_door"]} · 3-per-door: {line.breakdown["3_per_door"]} · 4-per-door: {line.breakdown["4_per_door"]}</p>}
              </div>
              <span className="font-mono text-sm text-slate-600">{line.size || "Bundled"}</span>
              <strong className="text-right font-mono text-lg text-slate-900" data-testid={`hardware-quantity-${index}`}>{line.quantity}</strong>
            </div>
          ))}
        </div>
        <div className="divide-y sm:hidden">
          {manifest.lines.map((line, index) => (
            <div key={line.line_id} className="p-3" data-testid={`hardware-mobile-line-${index}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="flex min-w-0 items-start gap-2 text-sm font-semibold text-slate-900"><PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /><span>{line.item_name}</span></p>
                <strong className="shrink-0 font-mono text-lg text-slate-900" data-testid={`hardware-mobile-quantity-${index}`}>{line.quantity}</strong>
              </div>
              <p className="mt-1 pl-6 font-mono text-xs text-slate-500">{line.size || "Bundled package"}</p>
              {line.breakdown && <p className="mt-2 rounded bg-slate-50 px-2 py-1.5 text-xs leading-5 text-slate-600">2-per-door: {line.breakdown["2_per_door"]}<br />3-per-door: {line.breakdown["3_per_door"]} · 4-per-door: {line.breakdown["4_per_door"]}</p>}
            </div>
          ))}
        </div>
        {!manifest.lines.length && <div className="px-4 py-12 text-center text-sm text-slate-400" data-testid="hardware-manifest-empty">Set cabinet Types to UPPER, BASE, PANTRY, DRAWER, or DRW BX to generate hardware.</div>}
      </div>
      <p className="text-xs text-slate-500">Hardware is grouped automatically from every Type = DOOR row and each DRAWER / DRW BX depth. No manual manifest rows are required.</p>
    </div>
  );
};